import React, { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionListRenderItemInfo, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { Odds } from '@/components/Odds';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { BetslipModalHeader } from '@/feature/betslip-sbk/components/BetslipModalHeader';
import { betslipModalStyles } from '@/feature/betslip-sbk/components/shared-modal-styles';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { ClosedSelection } from '@/feature/betslip-sbk/types';
import { formatEventTeams } from '@/feature/betslip-sbk/utils/formatter';
import { useMarketName } from '@/feature/event-details-sbk/hooks/use-market-name';
import { useOptionName } from '@/feature/event-details-sbk/hooks/use-option-name';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import {
    BottomSheetFooter,
    BottomSheetFooterProps,
    BottomSheetModal,
    BottomSheetSectionList,
} from '@gorhom/bottom-sheet';

import { BetSlipModalStickyHeaderRow } from '../BetSlipModalStickyHeaderRow';

export type PicksRemovedModalRef = {
    show: (onDismiss?: () => void) => void;
    dismiss: () => void;
};

const MODAL_ID = 'picksRemovedModal';

const styles = StyleSheet.create({
    marketLineSeparator: {
        height: 16,
    },
});

const PicksRemovedItem = ({ selection }: { selection: ClosedSelection }) => {
    const { option, market, event } = selection;

    const optionName = useOptionName({
        optionType: option.optionType,
        marketType: market.marketType,
        homeTeam: event.home_team,
        awayTeam: event.away_team,
        fallback: option.description,
        sportName: event.sport.name,
    });

    const marketName = useMarketName({
        sportName: event.sport.name,
        marketType: market.marketType,
        event,
        player: market.player,
        appendPlayerName: true,
        appendTeamName: !market.isMicroMarket,
        appendMicroMarketDescription: market.isMicroMarket,
        isExtendedName: true,
        fallback: market.description,
    });

    const eventTeams = formatEventTeams(event);

    return (
        <Box paddingVertical="s16" borderBottomWidth={1} borderBottomColor={ds.colorToken.border.strong} height={80}>
            <Row justifyContent="space-between" alignItems="center">
                <Box flexShrink={1}>
                    <Text variant="bodySmall" color="neutral300">
                        {eventTeams}
                    </Text>
                    <Row gap="s8" mt="s4" alignItems="center" flexShrink={1}>
                        <Text variant="bodyMedium" color={ds.colorToken.text.primary}>
                            {optionName}
                        </Text>
                        <LineSeparator vertical style={styles.marketLineSeparator} />
                        <Box flexShrink={1}>
                            <Text variant="bodyMedium" color="neutral300" numberOfLines={1}>
                                {marketName}
                            </Text>
                        </Box>
                    </Row>
                </Box>
                <Odds odds={option.odds} variant="bodyMedium" color={ds.colorToken.text.primary} ml="s12" />
            </Row>
        </Box>
    );
};

type SectionData = {
    title: string;
    data: ClosedSelection[];
};

const keyExtractor = (selection: ClosedSelection) => selection.option.id;

export const PicksRemovedModal = React.forwardRef<PicksRemovedModalRef, object>((_props, ref) => {
    const modalRef = useRef<BottomSheetModal>(null);
    const { t } = useTranslation(['betslip_sbk', 'common']);
    const isPredictions = useIsPredictionsProduct();
    const { bottom } = useSafeAreaInsets();
    const onDismissRef = useRef<(() => void) | undefined>(undefined);

    const closedSelections = useSbkBetSlipStore(state => state.closedSelections);

    const sections: SectionData[] = useMemo(
        () => [{ title: 'removed_picks', data: closedSelections }],
        [closedSelections]
    );

    useImperativeHandle(ref, () => ({
        show: (onDismiss?: () => void) => {
            onDismissRef.current = onDismiss;
            modalRef.current?.present();
        },
        dismiss: () => {
            modalRef.current?.dismiss();
        },
    }));

    const renderItem = useCallback(
        ({ item }: SectionListRenderItemInfo<ClosedSelection>) => <PicksRemovedItem selection={item} />,
        []
    );

    const handleGotIt = useCallback(() => {
        modalRef.current?.dismiss();
    }, []);

    const handleDismiss = useCallback(() => {
        onDismissRef.current?.();
    }, []);

    const footerStyle = useMemo(() => ({ paddingBottom: bottom + 16 || 16 }), [bottom]);

    const renderListHeader = useCallback(
        () => <BetslipModalHeader title={t('picks_removed')} description={t('picks_removed_description')} />,
        [t]
    );

    const renderSectionHeader = useCallback(
        () => (
            <BetSlipModalStickyHeaderRow>
                <Text variant="bodySmall" color="neutral300">
                    {t('removed_picks')}
                </Text>
                <Text variant="bodySmall" color="neutral300">
                    {isPredictions ? t('common:price') : t('odds')}
                </Text>
            </BetSlipModalStickyHeaderRow>
        ),
        [t, isPredictions]
    );

    const footerComponent = useCallback(
        (props: BottomSheetFooterProps) => (
            <BottomSheetFooter {...props} style={betslipModalStyles.contentContainer}>
                <Box px="s16" pt="s16" style={footerStyle}>
                    <Button label={t('common:got_it')} hierarchy="primary" onPress={handleGotIt} />
                </Box>
            </BottomSheetFooter>
        ),
        [t, handleGotIt, footerStyle]
    );

    return (
        <Modal
            id={MODAL_ID}
            modalRef={modalRef}
            hasBottomSheetView={false}
            onDismiss={handleDismiss}
            footerComponent={footerComponent}
        >
            <BottomSheetSectionList
                sections={sections}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                renderSectionHeader={renderSectionHeader}
                ListHeaderComponent={renderListHeader}
                stickySectionHeadersEnabled={true}
                contentContainerStyle={betslipModalStyles.listContentLargerPadding}
                showsVerticalScrollIndicator={showScrollIndicator}
                enableFooterMarginAdjustment={true}
            />
        </Modal>
    );
});
